import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  Hammer, 
  HardHat, 
  Home,
  Layers, 
  Wrench, 
  Paintbrush,
  Zap,
  Droplets,
  Building2,
  Square,
  Grid3X3,
  Bath,
  LayoutGrid,
  ArrowUpFromLine
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import SEOHead from '../components/common/SEOHead';

const WORK_IMAGE = 'https://customer-assets.emergentagent.com/job_b8d07c5d-fcca-4dc8-ad54-fc1da9664427/artifacts/scoo2t6q_IMAGENS%20PARA%20SITE8.png';
const WORK_IMAGE_2 = 'https://customer-assets.emergentagent.com/job_fort-build-uk/artifacts/phfmtmuf_IMAGENS%20PARA%20SITE4.png';
const WORK_IMAGE_3 = 'https://customer-assets.emergentagent.com/job_fort-build-uk/artifacts/9v031k8k_IMAGENS%20PARA%20SITE9.png';
const WORK_IMAGE_4 = 'https://customer-assets.emergentagent.com/job_fort-build-uk/artifacts/78rddx9j_IMAGENS%20PARA%20SITE10.png';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: 'easeOut' }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const staggerItem = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4 } }
};

const ServicesPage = () => {
  const { t } = useLanguage();

  const constructionServices = [
    { icon: Hammer, key: 'demolition' },
    { icon: HardHat, key: 'roofing' },
    { icon: Home, key: 'extensions' },
    { icon: Layers, key: 'flooring' },
    { icon: Building2, key: 'metalStructures' },
    { icon: Square, key: 'plastering' },
    { icon: LayoutGrid, key: 'plasterboard' },
    { icon: Zap, key: 'electrical' },
    { icon: Droplets, key: 'plumbing' },
    { icon: Wrench, key: 'civil' },
  ];

  const renovationServices = [
    { icon: LayoutGrid, key: 'plasterboard' },
    { icon: Grid3X3, key: 'tiling' },
    { icon: Layers, key: 'laminate' },
    { icon: Bath, key: 'bathroom' },
    { icon: Paintbrush, key: 'painting' },
    { icon: Home, key: 'extensions' },
    { icon: ArrowUpFromLine, key: 'loft' },
  ];

  const ServiceCard = ({ icon: Icon, serviceKey, category }) => {
    const title = t(`services.${category}.items.${serviceKey}.title`);
    const desc = t(`services.${category}.items.${serviceKey}.desc`);
    
    return (
      <motion.div
        variants={staggerItem}
        className="group relative bg-white border border-gray-100 p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden"
        data-testid={`service-${category}-${serviceKey}`}
      >
        <div className="absolute top-0 left-0 w-1 h-full bg-gold-main transform scale-y-0 group-hover:scale-y-100 transition-transform duration-300 origin-top" />
        <Icon className="w-10 h-10 text-gold-main mb-5" strokeWidth={1.5} />
        <h3 className="font-alatsi text-xl text-navy-deep mb-3">{title}</h3>
        <p className="text-slate-600 text-sm leading-relaxed">{desc}</p>
      </motion.div>
    );
  };

  return (
    <>
      <SEOHead 
        title={t('meta.services.title')}
        description={t('meta.services.description')}
      />

      {/* Hero Section */}
      <section 
        className="relative pt-32 pb-20 bg-navy-deep"
        data-testid="services-hero"
      >
        <div className="container-main">
          <div className="max-w-3xl">
            <motion.span 
              className="text-gold-main text-sm font-semibold uppercase tracking-widest block mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {t('services.label')}
            </motion.span>
            <motion.h1 
              className="font-alatsi text-5xl md:text-6xl text-white leading-tight mb-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              data-testid="services-title"
            >
              {t('services.title')}
            </motion.h1>
            <motion.p 
              className="text-xl text-gray-300 leading-relaxed"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {t('overview.description')}
            </motion.p>
          </div>
        </div>
      </section>

      {/* General Construction Section */}
      <section className="section-padding bg-white" data-testid="construction-section">
        <div className="container-main">
          <motion.div 
            className="mb-12"
            {...fadeUp}
          >
            <span className="label-text block mb-4">Section 1</span>
            <h2 className="font-alatsi text-4xl md:text-5xl text-navy-deep">
              {t('services.construction.title')}
            </h2>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            {constructionServices.map((service, index) => (
              <ServiceCard 
                key={index}
                icon={service.icon}
                serviceKey={service.key}
                category="construction"
              />
            ))}
          </motion.div>
        </div>
      </section>

      {/* Divider Image */}
      <section className="py-0 grid grid-cols-1 md:grid-cols-3">
        <img 
          src={WORK_IMAGE}
          alt="Construction welding work"
          className="w-full h-48 md:h-64 object-cover"
        />
        <img 
          src={WORK_IMAGE_2}
          alt="Drilling work"
          className="w-full h-48 md:h-64 object-cover"
        />
        <img 
          src={WORK_IMAGE_3}
          alt="Team work"
          className="w-full h-48 md:h-64 object-cover"
        />
      </section>

      {/* General Renovation Section */}
      <section className="section-padding bg-slate-50" data-testid="renovation-section">
        <div className="container-main">
          <motion.div 
            className="mb-12"
            {...fadeUp}
          >
            <span className="label-text block mb-4">Section 2</span>
            <h2 className="font-alatsi text-4xl md:text-5xl text-navy-deep">
              {t('services.renovation.title')}
            </h2>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            {renovationServices.map((service, index) => (
              <ServiceCard 
                key={index}
                icon={service.icon}
                serviceKey={service.key}
                category="renovation"
              />
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-navy-deep" data-testid="services-cta">
        <div className="container-main text-center">
          <motion.h2 
            className="font-alatsi text-3xl md:text-4xl text-white mb-4"
            {...fadeUp}
          >
            {t('hero.title')}
          </motion.h2>
          <motion.p 
            className="text-gray-300 mb-8 max-w-2xl mx-auto"
            {...fadeUp}
          >
            {t('hero.subtitle').substring(0, 150)}...
          </motion.p>
          <motion.div {...fadeUp}>
            <Link
              to="/get-in-touch"
              className="inline-flex items-center space-x-3 bg-gold-main text-white hover:bg-gold-dark transition-colors duration-300 px-10 py-5 uppercase tracking-widest text-sm font-semibold"
              data-testid="services-cta-btn"
            >
              <span>{t('services.getQuote')}</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default ServicesPage;
